import { useQueries, useQuery, type UseQueryOptions } from '@tanstack/react-query';
import {
  getLibraryFilterValues,
  globalSearch,
  type LibraryFilterTypeEnum,
  type LibrarySearchResult,
  type PortfolioAccountStatusEnum,
  type SearchQuery,
  type TaxStatusEnum,
} from 'venn-api';
import { TABLE_PAGE_SIZE } from '../config/constants';
import { createPortfoliosLibraryFilterKey, createPortfoliosLibraryKey } from 'venn-utils';
import type { LibraryFilterOption } from '../../../../home-page/src/components';
import { range } from 'lodash';

export type SortSettings = {
  field: string;
  order: SearchQuery['order'];
};

export const PORTFOLIO_STATUSES: LibraryFilterOption<PortfolioAccountStatusEnum>[] = [
  { label: 'Open', value: 'OPEN' },
  { label: 'Closed', value: 'CLOSED' },
  { label: 'No Status', value: 'NO_STATUS' },
] as const;

export const TAX_STATUSES: LibraryFilterOption<TaxStatusEnum>[] = [
  { label: 'Taxable', value: 'TAXABLE' },
  { label: 'Tax-deferred', value: 'TAX_DEFERRED' },
  { label: 'Tax-exempt', value: 'TAX_EXEMPT' },
] as const;

export const getPillDataFromFilterOptions = <T>(selectedValues: T[], filterOptions: LibraryFilterOption<T>[]) =>
  selectedValues.map((selected) => ({
    name: filterOptions.find(({ value }) => value === selected)!.label,
    value: selected,
  }));

const getPageQuery = (
  page: number,
  search: string,
  households: string[] | undefined,
  clientNames: string[] | undefined,
  programs: string[] | undefined,
  accountStatuses: PortfolioAccountStatusEnum[] | undefined,
  taxStatuses: TaxStatusEnum[] | undefined,
  sortSettings: SortSettings | undefined,
): UseQueryOptions<LibrarySearchResult> => ({
  queryKey: createPortfoliosLibraryKey(
    page,
    search,
    households,
    clientNames,
    programs,
    accountStatuses,
    taxStatuses,
    sortSettings?.field,
    sortSettings?.order,
  ),
  queryFn: async () => {
    const { content } = await globalSearch({
      entityFilters: ['PORTFOLIO'],
      page,
      pageSize: TABLE_PAGE_SIZE,
      name: search,
      privateAssetSearchMode: 'ALL',
      households,
      clients: clientNames,
      programs,
      portfolioAccountStatuses: accountStatuses,
      taxStatuses,
      additionalSearchFields: ['householdName', 'clientName', 'ownerContexts.name'],
      sortBy: sortSettings?.field,
      order: sortSettings?.order,
    });

    return content;
  },
  staleTime: 5 * 60 * 1000,
  keepPreviousData: true,
});

export const usePortfoliosLibraryData = (
  pages: number,
  search: string,
  households: string[] | undefined,
  clientNames: string[] | undefined,
  programs: string[] | undefined,
  accountStatuses: PortfolioAccountStatusEnum[] | undefined,
  taxStatuses: TaxStatusEnum[] | undefined,
  sortSettings: SortSettings | undefined,
) => {
  const results = useQueries({
    queries: range(pages).map((page) =>
      getPageQuery(page + 1, search, households, clientNames, programs, accountStatuses, taxStatuses, sortSettings),
    ),
  });

  return {
    isLoading: results.some((result) => result.isFetching),
    results: results.flatMap((result) => result.data?.results ?? []),
    total: results[0]?.data?.total ?? 0,
  };
};

const useLibraryFilterValues = (filterType: LibraryFilterTypeEnum) => {
  const { isFetching, data } = useQuery(
    createPortfoliosLibraryFilterKey(filterType),
    async () => {
      const { content } = await getLibraryFilterValues(filterType);
      return content;
    },
    {
      staleTime: 5 * 60 * 1000,
      keepPreviousData: true,
    },
  );

  return {
    isLoading: isFetching,
    results: data,
  };
};

export const useClientNameLibraryFilterValues = () => {
  return useLibraryFilterValues('CLIENT');
};

export const useHouseHoldNameLibraryFilterValues = () => {
  return useLibraryFilterValues('HOUSEHOLD');
};

export const useProgramLibraryFilterValues = () => {
  return useLibraryFilterValues('PROGRAM');
};
